import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Text } from "components";
import { MotivationalMessageService } from "services";
import { MotivationalMessageCurrentResponse } from "types";

import classes from "./GreetingSection.module.css";
import { fetchGreetingWeather, GreetingWeather } from "./weather";

const GREETING_TIMEZONE = "Europe/Zurich";

const getGreetingTimeParts = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: GREETING_TIMEZONE,
  }).formatToParts(new Date());

  const getPartValue = (type: "hour" | "minute" | "second") =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");

  return {
    hour: getPartValue("hour"),
    minute: getPartValue("minute"),
    second: getPartValue("second"),
  };
};

const getGreetingTranslationKey = () => {
  const { hour } = getGreetingTimeParts();

  if (hour >= 5 && hour < 12) {
    return "dashboard.greeting.goodMorning";
  }

  if (hour >= 12 && hour < 18) {
    return "dashboard.greeting.goodDay";
  }

  return "dashboard.greeting.goodEvening";
};

const getMillisecondsUntilNextGreetingChange = () => {
  const { hour, minute, second } = getGreetingTimeParts();
  const currentTimeInSeconds = hour * 3600 + minute * 60 + second;

  let nextChangeInSeconds = 0;

  if (hour < 5) {
    nextChangeInSeconds = 5 * 3600;
  } else if (hour < 12) {
    nextChangeInSeconds = 12 * 3600;
  } else if (hour < 18) {
    nextChangeInSeconds = 18 * 3600;
  } else {
    nextChangeInSeconds = 29 * 3600;
  }

  return Math.max(nextChangeInSeconds - currentTimeInSeconds, 1) * 1000;
};

interface GreetingSectionProps {
  className?: string;
}

const getGreetingMessage = (data: MotivationalMessageCurrentResponse) => data.text.trim() || null;

const GreetingSection = ({ className }: GreetingSectionProps) => {
  const { t } = useTranslation();
  const [greetingKey, setGreetingKey] = useState(getGreetingTranslationKey);
  const [message, setMessage] = useState<string | null>(null);
  const [weather, setWeather] = useState<GreetingWeather | undefined>(undefined);

  useEffect(() => {
    let timeoutId = 0;

    const scheduleNextUpdate = () => {
      setGreetingKey(getGreetingTranslationKey());
      timeoutId = window.setTimeout(
        scheduleNextUpdate,
        getMillisecondsUntilNextGreetingChange(),
      );
    };

    timeoutId = window.setTimeout(
      scheduleNextUpdate,
      getMillisecondsUntilNextGreetingChange(),
    );

    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    fetchGreetingWeather(controller.signal)
      .then(setWeather)
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  useEffect(() => {
    let active = true;

    MotivationalMessageService.getCurrentMotivationalMessage()
      .then((data) => {
        if (!active) {
          return;
        }

        setMessage(getGreetingMessage(data));
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className={className}>
      <div className={classes.inner}>
        <div className={classes.content}>
          <Text className={classes.title}>{`${t(greetingKey)} 🌤️`}</Text>
          {message ? <Text className={classes.message}>{message}</Text> : null}
        </div>

        <div className={classes.weather}>
          <Text className={classes.weatherTemperature}>
            {weather?.temperature !== undefined ? `${weather.temperature} °C` : "-- °C"}
          </Text>
          <Text className={classes.weatherLabel}>
            {t(weather?.weatherLabelKey ?? "dashboard.weather.unknown")}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default GreetingSection;
